import React, { useState, useEffect, useMemo } from "react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { FileText, Download, Search, Filter } from "lucide-react";
import { toast } from "sonner";
import api from "../services/api";

export default function ReportsPage() {
  const [countries, setCountries] = useState([]);
  const [entities, setEntities] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [entitySearch, setEntitySearch] = useState("");

  // Filter state
  const [filters, setFilters] = useState({
    report_date: "",
    place_of_incorporation: "",
    incorporation_date_from: "",
    incorporation_date_to: "",
    entity_id: "",
    industry: ""
  });

  const industries = ["Technology", "Finance", "Manufacturing", "Healthcare", "Retail", "Real Estate", "Energy", "Telecommunications", "Transportation", "Construction"];

  useEffect(() => {
    api.get("/countries").then(res => setCountries(res.data.items || [])).catch(() => {});
    api.get("/entities?limit=200").then(res => setEntities(res.data.items || [])).catch(() => {});
  }, []);

  const filteredCountries = useMemo(() => {
    if (!countrySearch) return countries;
    return countries.filter(c => c.name.toLowerCase().includes(countrySearch.toLowerCase()));
  }, [countries, countrySearch]);

  const filteredEntities = useMemo(() => {
    if (!entitySearch) return entities;
    return entities.filter(e => e.name.toLowerCase().includes(entitySearch.toLowerCase()));
  }, [entities, entitySearch]);

  const handleSearch = async () => {
    setLoading(true);
    setSearched(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get("/reports/filtered", { params });
      setResults(res.data.items || []);
    } catch (error) {
      toast.error("Failed to generate report");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      const params = { format };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get("/reports/filtered/export", { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `filtered_report.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Report exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error(`Failed to export ${format.toUpperCase()}`);
    }
  };

  const handleClear = () => {
    setFilters({ report_date: "", place_of_incorporation: "", incorporation_date_from: "", incorporation_date_to: "", entity_id: "", industry: "" });
    setResults([]);
    setSearched(false);
    setCountrySearch("");
    setEntitySearch("");
  };

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900" data-testid="reports-page-title">Reports</h1>
          <p className="text-sm text-slate-500 mt-1">Generate filtered company reports with snapshot-aware data</p>
        </div>
        {results.length > 0 && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => handleExport("pdf")} data-testid="export-filtered-pdf"><Download className="w-4 h-4 mr-1" />PDF</Button>
            <Button variant="outline" size="sm" onClick={() => handleExport("docx")} data-testid="export-filtered-docx"><Download className="w-4 h-4 mr-1" />Word</Button>
            <Button variant="outline" size="sm" onClick={() => handleExport("xlsx")} data-testid="export-filtered-xlsx"><Download className="w-4 h-4 mr-1" />Excel</Button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="enterprise-card mb-6">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
          <Filter className="w-5 h-5 text-slate-500" />
          <h3 className="text-lg font-medium text-slate-900">Filters</h3>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <Label className="form-label">Report Date</Label>
            <Input type="date" value={filters.report_date} onChange={(e) => setFilters({...filters, report_date: e.target.value})} className="form-input" data-testid="filter-report-date" />
            <p className="text-xs text-slate-400 mt-1">Shows company state as of this date</p>
          </div>

          <div>
            <Label className="form-label">Place of Incorporation</Label>
            <Select value={filters.place_of_incorporation} onValueChange={(v) => setFilters({...filters, place_of_incorporation: v === "all" ? "" : v})}>
              <SelectTrigger data-testid="filter-place"><SelectValue placeholder="All countries" /></SelectTrigger>
              <SelectContent>
                <div className="px-2 py-1">
                  <Input placeholder="Search country..." value={countrySearch} onChange={(e) => setCountrySearch(e.target.value)} className="form-input h-8 text-sm" data-testid="country-search" />
                </div>
                <SelectItem value="all">All Countries</SelectItem>
                {filteredCountries.slice(0, 50).map(c => (
                  <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="form-label">Industry</Label>
            <Select value={filters.industry} onValueChange={(v) => setFilters({...filters, industry: v === "all" ? "" : v})}>
              <SelectTrigger data-testid="filter-industry"><SelectValue placeholder="All industries" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Industries</SelectItem>
                {industries.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="form-label">Incorporation Date From</Label>
            <Input type="date" value={filters.incorporation_date_from} onChange={(e) => setFilters({...filters, incorporation_date_from: e.target.value})} className="form-input" data-testid="filter-incorp-from" />
          </div>

          <div>
            <Label className="form-label">Incorporation Date To</Label>
            <Input type="date" value={filters.incorporation_date_to} onChange={(e) => setFilters({...filters, incorporation_date_to: e.target.value})} className="form-input" data-testid="filter-incorp-to" />
          </div>

          <div>
            <Label className="form-label">Entity (Ownership Filter)</Label>
            <Select value={filters.entity_id} onValueChange={(v) => setFilters({...filters, entity_id: v === "all" ? "" : v})}>
              <SelectTrigger data-testid="filter-entity"><SelectValue placeholder="All entities" /></SelectTrigger>
              <SelectContent>
                <div className="px-2 py-1">
                  <Input placeholder="Search entity..." value={entitySearch} onChange={(e) => setEntitySearch(e.target.value)} className="form-input h-8 text-sm" data-testid="entity-search" />
                </div>
                <SelectItem value="all">All Entities</SelectItem>
                {filteredEntities.slice(0, 50).map(e => (
                  <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400 mt-1">Companies where entity has ownership</p>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex gap-2">
          <Button onClick={handleSearch} className="btn-primary" disabled={loading} data-testid="generate-report-btn">
            <Search className="w-4 h-4 mr-1" />{loading ? "Generating..." : "Generate Report"}
          </Button>
          <Button variant="outline" onClick={handleClear} data-testid="clear-filters-btn">Clear Filters</Button>
        </div>
      </div>

      {/* Results */}
      {searched && (
        <div className="enterprise-card">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <h3 className="text-lg font-medium text-slate-900">Results ({results.length} companies)</h3>
            {filters.report_date && (
              <span className="text-sm text-blue-800 bg-blue-50 px-3 py-1 rounded">
                Snapshot as of {filters.report_date}
              </span>
            )}
          </div>
          {results.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Industry</th>
                    <th>Place of Incorp.</th>
                    <th>Incorp. Date</th>
                    {filters.entity_id && <th>Ownership %</th>}
                    {filters.report_date && <th>Directors</th>}
                    {filters.report_date && <th>Shareholders</th>}
                  </tr>
                </thead>
                <tbody>
                  {results.map((item) => (
                    <tr key={item.id} data-testid={`report-row-${item.id}`}>
                      <td className="font-medium">{item.company_code}</td>
                      <td>{item.name}</td>
                      <td><span className={`px-2 py-0.5 text-xs rounded-full ${item.status === 'active' ? 'bg-green-100 text-green-700' : item.status === 'dormant' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>{item.status}</span></td>
                      <td>{item.industry}</td>
                      <td>{item.place_of_incorporation || "N/A"}</td>
                      <td>{item.incorporation_date}</td>
                      {filters.entity_id && <td>{item.entity_ownership?.percentage?.toFixed(2) || 0}%</td>}
                      {filters.report_date && <td>{item.director_count || 0}</td>}
                      {filters.report_date && <td>{item.shareholder_count || 0}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No companies match the selected filters</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
